import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import '@lotlinx-aws/sparky-component-library/Sparky/styles/reset.scss';

import { createBrowserRouter } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import { AuthShell } from '@lotlinx-aws/auth-shell';
import { initObservability } from '@lotlinx-aws/observability-react';

import ROUTES from 'routes';
import { subscribePiiClearOnLogout } from 'store/subscribePiiClearOnLogout';
import './index.scss';

subscribePiiClearOnLogout();

const isProd = import.meta.env.VITE_APP_DEPLOY_ENV === 'production';

initObservability({
  sentry: {
    dsn: 'https://4091e6da69c24d579d814f6c976274b4@o1421320.ingest.sentry.io/6767175',
    environment: import.meta.env.VITE_APP_DEPLOY_ENV,
    release: import.meta.env.VITE_APP_VERSION,
  },
  ga: { measurementId: isProd ? 'G-KVDHV24C57' : 'G-HWW2D8X7DK' },
  gtm: { gtmId: isProd ? 'GTM-NG5GDSRW' : 'GTM-K4P3SG65' },
  clarity: isProd ? { projectId: 'g9l30u34mm' } : undefined,
  webVitals: true,
});

(async () => {
  // Start MSW before AuthShell mounts so session restore keepalive is mocked.
  if (import.meta.env.VITE_APP_LOCAL_ENV === 'local') {
    (window as any).__MSW_READY__ = false;
    try {
      const { worker } = await import('./api/mocks/browser');
      const apiUrl = import.meta.env.VITE_APP_API_URL;
      await worker.start({
        // Bypass by default so an unmocked call still reaches the real dev API while developing.
        // The e2e suite sets VITE_APP_MSW_STRICT so the same call fails loudly instead.
        onUnhandledRequest(request, print) {
          if (
            import.meta.env.VITE_APP_MSW_STRICT === 'true' &&
            apiUrl &&
            request.url.startsWith(apiUrl)
          ) {
            print.error();
          }
        },
        serviceWorker: {
          url: '/mockServiceWorker.js',
        },
      });
      (window as any).__MSW_READY__ = true;
    } catch (error) {
      console.error('MSW: Failed to start Mock Service Worker:', error);
    }
  }

  const LDProvider = await asyncWithLDProvider({
    clientSideID: import.meta.env.VITE_APP_LD_ID ?? '',
    options: {
      application: { id: 'turn4', version: import.meta.env.VITE_APP_DEPLOY_ENV },
      diagnosticOptOut: true,
      bootstrap: 'localStorage',
    },
    reactOptions: {
      useCamelCaseFlagKeys: false,
    },
  });

  const router = createBrowserRouter(ROUTES);
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <AuthShell
        apiUrl={import.meta.env.VITE_APP_API_URL}
        deployEnv={import.meta.env.VITE_APP_DEPLOY_ENV}
        appId='Turn4'
      >
        <LDProvider>
          <RouterProvider router={router} />
        </LDProvider>
      </AuthShell>
      <div
        style={{ display: 'none' }}
        aria-hidden
        data-version={`${import.meta.env.VITE_APP_VERSION}-${import.meta.env.VITE_APP_DEPLOY_ENV}`}
      />
    </StrictMode>,
  );
})();
